import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import api from "../../utils/api";

export type EmployeeDashboardAccess = {
  portalRole: string;
  viewerEmpId?: number | null;
  viewerEmpRole?: string | null;
  viewerEmpName?: string | null;
};

export type DailyTrendPoint = {
  day: string;
  label: string;
  design: number;
  qc: number;
  redesign?: number;
  design_live?: number;
  qc_live?: number;
  design_redesign?: number;
  qc_redesign?: number;
  design_internal_live?: number;
  qc_internal_live?: number;
  design_internal_redesign?: number;
  qc_internal_redesign?: number;
  total: number;
};

export type MonthlyTrendPoint = {
  month_year: string;
  label: string;
  design: number;
  qc: number;
  redesign?: number;
  design_live?: number;
  qc_live?: number;
  design_redesign?: number;
  qc_redesign?: number;
  design_internal_live?: number;
  qc_internal_live?: number;
  design_internal_redesign?: number;
  qc_internal_redesign?: number;
  total: number;
  efficiency: number;
  work_days?: number;
  period_capacity?: number;
};

export type EmployeeStats = {
  emp_id: number;
  emp_name: string;
  email: string;
  role: string;
  shift: string;
  active: boolean;
  verified: boolean;
  capacity: number;
  period_capacity: number;
  work_days: number;
  cases_credited: number;
  cases_credited_day: number;
  cases_credited_week: number;
  cases_credited_month: number;
  total_units: number;
  design_units: number;
  qc_units: number;
  redesign_units: number;
  redesign_cases: number;
  live_units: number;
  design_units_live: number;
  qc_units_live: number;
  design_units_redesign: number;
  qc_units_redesign: number;
  design_units_internal_live: number;
  qc_units_internal_live: number;
  design_units_internal_redesign: number;
  qc_units_internal_redesign: number;
  units_day: number;
  units_week: number;
  units_month: number;
  design_per_day: number;
  design_efficiency: number;
  qc_efficiency: number;
  redesign_efficiency: number;
  qc_cases: number;
  mix_design: number;
  mix_qc: number;
  mix_redesign: number;
  rework_clean: number;
  rework_returned: number;
  pipeline_total: number;
  pipeline_design: number;
  pipeline_qc: number;
  pipeline_delivered: number;
  pipeline_branch_units: number;
  pipeline_branch_cases: number;
  efficiency_month: number;
  filter_start?: string;
  filter_end?: string;
  filter_year?: number | null;
  daily_trend?: DailyTrendPoint[];
  monthly_trend?: MonthlyTrendPoint[];
};

export type EmployeeDashboardFilters = {
  year?: number;
  startDate?: string;
  endDate?: string;
};

export type EmployeeCaseRow = {
  case_id: string;
  case_owner: string;
  case_status: string;
  allocation_status: string;
  type: string;
  units_family?: string;
  units_family_label?: string;
  units_work?: string;
  employee_name?: string;
  designer: string;
  captain: string;
  qc1: string;
  qc2: string;
  design_units: number;
  qc_units: number;
  total_units: number;
  details: string;
  created_at: string;
  updated_at: string;
  creation_time_ms?: number;
};

/** Lightweight roster row for admin pickers (no stats). */
export type EmployeeListItem = {
  emp_id: number;
  emp_name: string;
  email?: string;
  role: string;
  shift?: string;
  active?: boolean;
  overview_group?: "primary" | "secondary" | "other";
};

export type EmployeeCaseKind = "live" | "redesign" | "internal_redesign";

export type EmployeeRedesignReason = {
  case_id: string;
  kind: "redesign" | "internal_redesign";
  category: string | null;
  created_at: string | null;
  designed_by: string | null;
};

export type EmployeeCaseReportRow = {
  case_id: string;
  live_case_id: string;
  kind: EmployeeCaseKind;
  created_at: string | null;
  delivery_units: number | null;
  delivery_units_marked: boolean;
  design_unit: number | null;
  category: string | null;
  allocated_by: string | null;
  allocated_at: string | null;
  qc1_by: string | null;
  qc1_at: string | null;
  designed_by: string | null;
  designer_units: Record<string, number> | null;
  model_by?: string | null;
  model_units?: Record<string, number> | null;
  designed_at: string | null;
  qc2_by: string | null;
  qc2_at: string | null;
  upload_by: string | null;
  upload_at: string | null;
  upload_bg: "green" | "red" | null;
  has_reference: boolean;
  operational_date: string | null;
  came_back: boolean;
  came_back_internal: boolean;
  came_back_external: boolean;
  redesigns: EmployeeRedesignReason[];
  model_only?: boolean;
  filled_order_patients?: Array<{
    patient: string;
    name: string;
    units: number;
    marked: boolean;
  }> | null;
};

export type EmployeeCaseReportCategory = {
  category: string;
  kind: "internal" | "external";
  count: number;
};

export type EmployeeCaseReportDay = {
  date: string;
  designed_cases: number;
  /** Live cases this employee QC'd. */
  qc_cases?: number;
  /** Live cases designed or QC'd, counted once when both. */
  tracked_cases?: number;
  came_back: number;
  came_back_internal: number;
  came_back_external: number;
  redesign_pct: number | null;
  categories: EmployeeCaseReportCategory[];
};

export type EmployeeCaseReportSummary = {
  designed_cases: number;
  /** Live cases this employee QC'd. */
  qc_cases?: number;
  /** Live cases designed or QC'd, counted once when both. Came-back rate uses this. */
  tracked_cases?: number;
  designed_redesigns: number;
  came_back: number;
  came_back_internal: number;
  came_back_external: number;
  redesign_pct: number | null;
  categories: EmployeeCaseReportCategory[];
};

export type EmployeeCaseReport = {
  emp_id: number;
  emp_name: string;
  role: string;
  shift: string;
  startDate: string;
  endDate: string;
  window: { from: string; to: string };
  summary: EmployeeCaseReportSummary;
  days: EmployeeCaseReportDay[];
  rows: EmployeeCaseReportRow[];
};

export type EmployeeCaseHistory = {
  live_case_id: string;
  count: number;
  rows: EmployeeCaseReportRow[];
};

type DashboardState = {
  stats: EmployeeStats | null;
  team: EmployeeStats[];
  employees: EmployeeListItem[];
  cases: EmployeeCaseRow[];
  caseReport: EmployeeCaseReport | null;
  caseHistory: EmployeeCaseHistory | null;
  page: number;
  totalPages: number;
  total: number;
  selectedEmpId: number | null;
  loading: boolean;
  teamLoading: boolean;
  employeesLoading: boolean;
  casesLoading: boolean;
  caseReportLoading: boolean;
  caseHistoryLoading: boolean;
  error: string | null;
};

const initialState: DashboardState = {
  stats: null,
  team: [],
  employees: [],
  cases: [],
  caseReport: null,
  caseHistory: null,
  page: 1,
  totalPages: 0,
  total: 0,
  selectedEmpId: null,
  loading: false,
  teamLoading: false,
  employeesLoading: false,
  casesLoading: false,
  caseReportLoading: false,
  caseHistoryLoading: false,
  error: null,
};

const accessParams = (access: EmployeeDashboardAccess) => ({
  portalRole: access.portalRole,
  viewerEmpId: access.viewerEmpId ?? undefined,
  viewerEmpRole: access.viewerEmpRole ?? undefined,
  viewerEmpName: access.viewerEmpName ?? undefined,
});

export const fetchEmployeeStats = createAsyncThunk(
  "employeeDashboard/stats",
  async (
    payload: EmployeeDashboardAccess & {
      empId?: number | null;
      year?: number;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(`/allocation-tool/employee-dashboard/stats`, {
        params: {
          ...accessParams(payload),
          empId: payload.empId ?? undefined,
          year: payload.year ?? undefined,
          startDate: payload.startDate ?? undefined,
          endDate: payload.endDate ?? undefined,
        },
      });
      return res.data.data as EmployeeStats;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch employee stats"
        );
      }
      return rejectWithValue("Failed to fetch employee stats");
    }
  }
);

export const fetchTeamStats = createAsyncThunk(
  "employeeDashboard/team",
  async (
    payload: EmployeeDashboardAccess & {
      page?: number;
      search?: string;
      role?: string;
      limit?: number;
      year?: number;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(`/allocation-tool/employee-dashboard/team`, {
        params: {
          ...accessParams(payload),
          page: payload.page ?? 1,
          search: payload.search || undefined,
          role: payload.role || undefined,
          limit: payload.limit ?? 20,
          year: payload.year ?? undefined,
          startDate: payload.startDate ?? undefined,
          endDate: payload.endDate ?? undefined,
        },
      });
      return res.data.data as {
        employees: EmployeeStats[];
        page: number;
        totalPages: number;
        total: number;
      };
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch team stats"
        );
      }
      return rejectWithValue("Failed to fetch team stats");
    }
  }
);

export const fetchEmployeeList = createAsyncThunk(
  "employeeDashboard/employees",
  async (
    payload: EmployeeDashboardAccess & { search?: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(`/allocation-tool/employee-dashboard/employees`, {
        params: {
          ...accessParams(payload),
          search: payload.search || undefined,
        },
      });
      return res.data.data as { employees: EmployeeListItem[] };
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch employees"
        );
      }
      return rejectWithValue("Failed to fetch employees");
    }
  }
);

export const fetchEmployeeCases = createAsyncThunk(
  "employeeDashboard/cases",
  async (
    payload: EmployeeDashboardAccess & {
      empId?: number | null;
      page?: number;
      search?: string;
      caseType?: string;
      limit?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(`/allocation-tool/employee-dashboard/cases`, {
        params: {
          ...accessParams(payload),
          empId: payload.empId ?? undefined,
          page: payload.page ?? 1,
          search: payload.search || undefined,
          caseType: payload.caseType || undefined,
          limit: payload.limit ?? 20,
        },
      });
      return res.data.data as {
        cases: EmployeeCaseRow[];
        page: number;
        totalPages: number;
        total: number;
      };
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch employee cases"
        );
      }
      return rejectWithValue("Failed to fetch employee cases");
    }
  }
);

export const fetchEmployeeCaseReport = createAsyncThunk(
  "employeeDashboard/caseReport",
  async (
    payload: EmployeeDashboardAccess & {
      empId?: number | null;
      startDate: string;
      endDate: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(`/allocation-tool/employee-dashboard/case-report`, {
        params: {
          ...accessParams(payload),
          empId: payload.empId ?? undefined,
          startDate: payload.startDate,
          endDate: payload.endDate,
        },
        timeout: 180000,
      });
      return res.data.data as EmployeeCaseReport;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch employee case report"
        );
      }
      return rejectWithValue("Failed to fetch employee case report");
    }
  }
);

export const fetchEmployeeCaseHistory = createAsyncThunk(
  "employeeDashboard/caseHistory",
  async (
    payload: EmployeeDashboardAccess & {
      empId?: number | null;
      live_case_id: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(`/allocation-tool/employee-dashboard/case-history`, {
        params: {
          ...accessParams(payload),
          empId: payload.empId ?? undefined,
          live_case_id: payload.live_case_id,
        },
        timeout: 120000,
      });
      return res.data.data as EmployeeCaseHistory;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch case history"
        );
      }
      return rejectWithValue("Failed to fetch case history");
    }
  }
);

const employeeDashboardSlice = createSlice({
  name: "employeeDashboard",
  initialState,
  reducers: {
    setSelectedEmpId(state, action) {
      state.selectedEmpId = action.payload;
    },
    clearEmployeeDashboard(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeeStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
        state.selectedEmpId = action.payload.emp_id;
      })
      .addCase(fetchEmployeeStats.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load stats";
      })
      .addCase(fetchTeamStats.pending, (state) => {
        state.teamLoading = true;
        state.error = null;
      })
      .addCase(fetchTeamStats.fulfilled, (state, action) => {
        state.teamLoading = false;
        state.team = action.payload.employees;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.total = action.payload.total;
      })
      .addCase(fetchTeamStats.rejected, (state, action) => {
        state.teamLoading = false;
        state.error = (action.payload as string) || "Failed to load team";
      })
      .addCase(fetchEmployeeList.pending, (state) => {
        state.employeesLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeList.fulfilled, (state, action) => {
        state.employeesLoading = false;
        state.employees = action.payload.employees;
      })
      .addCase(fetchEmployeeList.rejected, (state, action) => {
        state.employeesLoading = false;
        state.error = (action.payload as string) || "Failed to load employees";
      })
      .addCase(fetchEmployeeCases.pending, (state) => {
        state.casesLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeCases.fulfilled, (state, action) => {
        state.casesLoading = false;
        state.cases = action.payload.cases;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.total = action.payload.total;
      })
      .addCase(fetchEmployeeCases.rejected, (state, action) => {
        state.casesLoading = false;
        state.error = (action.payload as string) || "Failed to load cases";
      })
      .addCase(fetchEmployeeCaseReport.pending, (state) => {
        state.caseReportLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeCaseReport.fulfilled, (state, action) => {
        state.caseReportLoading = false;
        state.caseReport = action.payload;
        state.selectedEmpId = action.payload.emp_id;
      })
      .addCase(fetchEmployeeCaseReport.rejected, (state, action) => {
        state.caseReportLoading = false;
        state.error = (action.payload as string) || "Failed to load case report";
      })
      .addCase(fetchEmployeeCaseHistory.pending, (state) => {
        state.caseHistoryLoading = true;
      })
      .addCase(fetchEmployeeCaseHistory.fulfilled, (state, action) => {
        state.caseHistoryLoading = false;
        state.caseHistory = action.payload;
      })
      .addCase(fetchEmployeeCaseHistory.rejected, (state, action) => {
        state.caseHistoryLoading = false;
        state.error = (action.payload as string) || "Failed to load case history";
      });
  },
});

export const { setSelectedEmpId, clearEmployeeDashboard } =
  employeeDashboardSlice.actions;
export default employeeDashboardSlice.reducer;
